package dev.youndie.site.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initManiPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("mani.md", FrontMatterElement.Builder { addScalar("title", "mani"); addScalar("searchTitle", "Kotlin Multiplatform full-stack demo — Compose clients, JVM and native Ktor server"); addScalar("description", "A budget planner written end to end in Kotlin: Compose clients for Android, iOS, desktop and the browser, and a Ktor server that compiles to both the JVM and a native binary."); addScalar("library", "mani"); addScalar("post", "/blog/the-screenshot-is-the-test"); }))
}

@Page("/mani")
@Layout(".components.layouts.LibraryLayout")
@Composable
fun ManiPage() {
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-it-is") }) {
        org.jetbrains.compose.web.dom.Text("What it is")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A demo on purpose. It exists to show what a full Kotlin stack looks like when every part of it is real rather than sketched — Compose Multiplatform clients for Android, iOS, desktop and the browser, a Ktor server that compiles ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("both")
        }
        org.jetbrains.compose.web.dom.Text(" to the JVM and to a native Linux binary, and one shared module holding the API contract for all of them.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Live at ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://mani.kotlin.website") {
            org.jetbrains.compose.web.dom.Text("mani.kotlin.website")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-parts-worth-copying") }) {
        org.jetbrains.compose.web.dom.Text("The parts worth copying")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("The same code on both sides of the wire.")
        }
        org.jetbrains.compose.web.dom.Text(" The same ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("@Resource")
        }
        org.jetbrains.compose.web.dom.Text(" classes route requests on the server and build URLs on the client, so a renamed path moves both ends together — there is no place for a string on one side to disagree with a string on the other. The same code signs a token in the JVM build and in the native build.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("The README screenshot is a golden.")
        }
        org.jetbrains.compose.web.dom.Text(" The image at the top of the repository is not a screenshot somebody committed; it ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("is")
        }
        org.jetbrains.compose.web.dom.Text(" one of the files the screenshot tests compare against. Re-recording the goldens redraws it in the same commit, so it cannot quietly go stale — if the picture is old, the tests are red.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Two of its dependencies were written for it.")
        }
        org.jetbrains.compose.web.dom.Text(" ")
        com.varabyte.kobweb.silk.components.navigation.Link("/mongkn") {
            org.jetbrains.compose.web.dom.Text("mongkn")
        }
        org.jetbrains.compose.web.dom.Text(", because the native server build has to talk to the same database as the JVM one and no official driver exists, and ")
        com.varabyte.kobweb.silk.components.navigation.Link("/viddik") {
            org.jetbrains.compose.web.dom.Text("viddik")
        }
        org.jetbrains.compose.web.dom.Text(" for the goldens above.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-it-is-not") }) {
        org.jetbrains.compose.web.dom.Text("What it is not")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("No email confirmation, no password recovery, no rate limiting, and passwords hashed with salted SHA-256 rather than a slow KDF. Saying so is the point: a demo that quietly omits the security work invites someone to copy it, and the omissions are exactly what a reader cannot see. The public instance is a playground.")
    }
}
